import { initializeApp, type FirebaseApp } from 'firebase/app';
import {
  initializeAuth,
  indexedDBLocalPersistence,
  browserLocalPersistence,
  browserPopupRedirectResolver,
  type Auth,
  type User,
} from 'firebase/auth';

// Firebase client config (tilework project)
const firebaseConfig = {
  apiKey: 'AIzaSyC54HqlGrkyANVFKGDQi3LobO5moDOuafk',
  authDomain: 'tilework-e18c5.firebaseapp.com',
  projectId: 'tilework-e18c5',
  storageBucket: 'tilework-e18c5.firebasestorage.app',
  messagingSenderId: '199991289749',
  appId: '1:199991289749:web:d7ac38af24e1f29251f89c',
  measurementId: 'G-3YXYKJK38T',
};

export class FirebaseProvider {
  private static instance: FirebaseProvider;
  private _app: FirebaseApp | null = null;
  private _auth: Auth | null = null;
  private _authReadyPromise: Promise<User | null> | null = null;
  private _authReadyResolve: ((user: User | null) => void) | null = null;
  private _onAuthStateChangeCallback: ((user: User | null) => void) | null =
    null;
  private _tokenRefreshInterval: ReturnType<typeof setInterval> | null = null;

  public static getInstance(): FirebaseProvider {
    if (!FirebaseProvider.instance) {
      FirebaseProvider.instance = new FirebaseProvider();
    }
    return FirebaseProvider.instance;
  }

  public get app(): FirebaseApp {
    if (!this._app) {
      throw new Error('Firebase app not initialized. Call configure() first.');
    }
    return this._app;
  }

  public get auth(): Auth {
    if (!this._auth) {
      throw new Error(
        'Firebase auth not initialized. Call configure() first.',
      );
    }
    return this._auth;
  }

  public configure(): void {
    if (!this._app) {
      try {
        this._app = initializeApp(firebaseConfig);
        this._auth = initializeAuth(this._app, {
          persistence: [indexedDBLocalPersistence, browserLocalPersistence],
          popupRedirectResolver: browserPopupRedirectResolver,
        });

        // Create promise that resolves when auth state is first determined
        this._authReadyPromise = new Promise<User | null>((resolve) => {
          this._authReadyResolve = resolve;
        });

        // Set up auth state listener to handle token refresh and initial auth ready
        this._auth.onAuthStateChanged(async (user) => {
          // Resolve the auth ready promise on first call
          if (this._authReadyResolve) {
            this._authReadyResolve(user);
            this._authReadyResolve = null;
          } else if (this._onAuthStateChangeCallback) {
            this._onAuthStateChangeCallback(user);
          }

          // Clear any existing token refresh interval
          if (this._tokenRefreshInterval != null) {
            clearInterval(this._tokenRefreshInterval);
            this._tokenRefreshInterval = null;
          }

          if (user) {
            // Force token refresh every 50 minutes (token expires in 1 hour)
            this._tokenRefreshInterval = setInterval(async () => {
              try {
                await user.getIdToken(true);
              } catch (error) {
                console.error('Error refreshing auth token:', error);
                throw new Error(
                  `Error refreshing auth token: ${error}. Please refresh the page.`,
                );
              }
            }, 50 * 60 * 1000);
          }
        });
      } catch (error) {
        console.error('Firebase initialization failed:', error);
        throw new Error(`Firebase initialization failed: ${error}`);
      }
    }
  }

  public onAuthStateChange(callback: (user: User | null) => void): void {
    this._onAuthStateChangeCallback = callback;
  }

  public waitForAuthReady(): Promise<User | null> {
    if (!this._authReadyPromise) {
      throw new Error('Firebase not configured. Call configure() first.');
    }
    return this._authReadyPromise;
  }
}

export const configureFirebase = (): void => {
  FirebaseProvider.getInstance().configure();
};

export const getFirebase = (): FirebaseProvider => {
  return FirebaseProvider.getInstance();
};
